import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Box, FormControl, MenuItem, Select } from '@mui/material';

export const pickerValuesMap = {
  custom: 'custom',
  today: 'today',
  tomorrow: 'tomorrow',
  mtd: 'mtd',
  ytd: 'ytd',
  last7Days: 7,
  last30Days: 30,
  lastYear: 364,
};

export const pickerVariants = {
  [pickerValuesMap.today]: () => ({
    start_dt: moment().startOf('day'),
    end_dt: moment(),
  }),

  [pickerValuesMap.tomorrow]: () => ({
    start_dt: moment().add(1, 'day').startOf('day'),
    end_dt: moment().add(1, 'day').endOf('day'),
  }),

  [pickerValuesMap.mtd]: () => ({
    start_dt: moment().startOf('month'),
    end_dt: moment(),
  }),

  [pickerValuesMap.ytd]: () => ({
    start_dt: moment().startOf('year'),
    end_dt: moment(),
  }),

  year: (yearValue) => {
    const year = parseInt(yearValue);
    return {
      start_dt: moment(`${year}-01-01`).startOf('day'),
      end_dt: moment(`${year}-12-31`).endOf('day'),
    };
  },
};

export const pickerLabels = {
  [pickerValuesMap.custom]: 'Custom range',
  [pickerValuesMap.last7Days]: 'Last 7 days',
  [pickerValuesMap.last30Days]: 'Last 30 days',
  [pickerValuesMap.lastYear]: 'Last year',
  [pickerValuesMap.today]: 'Today',
  [pickerValuesMap.tomorrow]: 'Tomorrow',
  [pickerValuesMap.mtd]: 'Month to date',
  [pickerValuesMap.ytd]: 'Year to date',
};

// Generate last 2 years including the current year
export const generateYearOptions = (yearsCount = 2) => {
  const currentYear = moment().year();
  const years = [];

  for (let year = currentYear - (yearsCount - 1); year <= currentYear; year++) {
    years.push({ value: year.toString(), label: year });
  }

  return years;
};

const detectDateRangeFromParams = (
  fromDateParam,
  toDateParam,
  dateRangeVariants = pickerVariants,
  dateRangeMap = pickerValuesMap
) => {
  if (!fromDateParam || !toDateParam) {
    return null;
  }

  const fromDate = moment(fromDateParam);
  const toDate = moment(toDateParam);

  // Check for exact matches with dateRangeVariants
  for (const [key, variant] of Object.entries(dateRangeVariants)) {
    if (typeof variant === 'function') {
      const { start_dt, end_dt } = variant();
      // Compare dates with some tolerance (same day)
      if (start_dt.isSame(fromDate, 'day') && end_dt.isSame(toDate, 'day')) {
        return dateRangeMap[key];
      }
    }
  }

  // Check for numeric day ranges (last7Days, last30Days, lastYear)
  const daysDiff = toDate.diff(fromDate, 'days');
  const today = moment();

  // Check if toDate is today (or very close) and calculate days back
  if (
    toDate.isSame(today, 'day') ||
    Math.abs(toDate.diff(today, 'days')) <= 1
  ) {
    for (const [key, value] of Object.entries(dateRangeMap)) {
      if (typeof value === 'number' && value === daysDiff) {
        return dateRangeMap[key];
      }
    }
  }

  // Check for year ranges (e.g., 2023, 2024)
  const fromYear = fromDate.year();
  const toYear = toDate.year();

  if (
    fromYear === toYear &&
    fromDate.isSame(moment(`${fromYear}-01-01`), 'day') &&
    toDate.isSame(moment(`${toYear}-12-31`), 'day')
  ) {
    return fromYear.toString();
  }

  // If no match found, return 'custom'
  return pickerValuesMap.custom;
};

export const setDateRangeInUrl = (value, dateRangeVariants, setParams) => {
  if (!value || value === pickerValuesMap.custom) return;

  setParams((prev) => {
    const changeParams = ({ start_dt, end_dt }) => {
      prev.set('fromDate', start_dt.toDate().toISOString());
      prev.set('toDate', end_dt.toDate().toISOString());
    };

    if (typeof value === 'number') {
      const start_dt = moment().subtract(value, 'days').startOf('day');
      const end_dt = moment().endOf('day');
      changeParams({ start_dt, end_dt });
    } else if (dateRangeVariants[value]) {
      const { start_dt, end_dt } = dateRangeVariants[value]();
      changeParams({ start_dt, end_dt });
    } else if (value.match && value.match(/^\d{4}$/)) {
      changeParams(pickerVariants.year(value));
    }

    return prev;
  });
};


export function useDateRangeFilter({
  dateRangeVariants = pickerVariants,
  dateRangeMap = pickerValuesMap,
  defaultValue = null, //pickerValuesMap[something]
} = {}) {
  const [params, setParams] = useSearchParams();
  const fromDateParam = params.get('fromDate');
  const toDateParam = params.get('toDate');

  const detectedDateRange = useMemo(() => {
    const detected = detectDateRangeFromParams(
      fromDateParam,
      toDateParam,
      dateRangeVariants,
      dateRangeMap
    );
    return detected || defaultValue || pickerValuesMap.custom;
  }, [
    fromDateParam,
    toDateParam,
    dateRangeVariants,
    dateRangeMap,
    defaultValue,
  ]);
  
  const [dateRange, setDateRange] = useState(detectedDateRange);

  const isCustomDateRange = dateRange === pickerValuesMap.custom;
  
  // setDefault state
  useEffect(() => {
    if (!fromDateParam && !toDateParam && defaultValue) {
      setDateRangeInUrl(defaultValue, dateRangeVariants, setParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  
  const fromDate = useMemo(
    () => (fromDateParam ? new Date(fromDateParam) : null),
    [fromDateParam]
  );

  const toDate = useMemo(
    () => (toDateParam ? new Date(toDateParam) : null),
    [toDateParam]
  );

  const handleChangeFromDate = useCallback(
    (value) => {
      setDateRange(pickerValuesMap.custom);
      setParams((prev) => {
        if (value)
          prev.set(
            'fromDate',
            value.toString() === 'Invalid Date' ? value : value.toISOString()
          );
        if (value && toDate && new Date(toDate) < new Date(value)) {
          prev.set('toDate', value.toISOString());
        }
        return prev;
      });
    },
    [setParams, toDate]
  );

  const handleChangeToDate = useCallback(
    (value) => {
      setDateRange(pickerValuesMap.custom);
      setParams((prev) => {
        if (value)
          prev.set(
            'toDate',
            value.toString() === 'Invalid Date' ? value : value.toISOString()
          );
        if (value && fromDate && new Date(fromDate) > new Date(value)) {
          prev.set('fromDate', value.toISOString());
        }
        return prev;
      });
    },
    [setParams, fromDate]
  );

  const handleChangePeriod = useCallback(
    (e) => {
      const value = e.target.value;
      setDateRange(value);
      setDateRangeInUrl(value, dateRangeVariants, setParams);
    },
    [setParams]
  );

  const onClear = useCallback(() => {
    params.delete('fromDate');
    params.delete('toDate');
    setParams(params);

    setDateRange(defaultValue || pickerValuesMap.custom);
  }, [defaultValue, params]);

  return {
    fromDate,
    toDate,
    handleChangePeriod,
    handleChangeToDate,
    handleChangeFromDate,
    isCustomDateRange,
    dateRange,
    onClear,
  };
}

export const StartDatePicker = ({
  fromDate,
  handleChangeFromDate,
  toDate,
  isCustomDateRange,
}) => {
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        value={fromDate}
        onChange={handleChangeFromDate}
        maxDate={toDate}
        disabled={!isCustomDateRange}
        sx={{
          width: '100%',
          '& .MuiInputBase-root': {
            height: '36px',
            fontSize: '14px',
          },
        }}
        slotProps={{ textField: { size: 'small', label: 'Start date' } }}
        slots={{ OpenPickerIcon: CalendarTodayIcon }}
      />
    </LocalizationProvider>
  );
};

export const EndDatePicker = ({
  toDate,
  handleChangeToDate,
  fromDate,
  isCustomDateRange,
}) => {
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        value={toDate}
        onChange={handleChangeToDate}
        minDate={fromDate}
        disabled={!isCustomDateRange}
        disableFuture={isCustomDateRange}
        sx={{
          width: '100%',
          '& .MuiInputBase-root': {
            height: '36px',
            fontSize: '14px',
          },
        }}
        slotProps={{ textField: { size: 'small', label: 'End date' } }}
        slots={{ OpenPickerIcon: CalendarTodayIcon }}
      />
    </LocalizationProvider>
  );
};

export const PeriodSelector = ({ options, dateRange, handleChangePeriod }) => {
  return (
    <FormControl sx={{ width: '100%' }} size="small">
      <Select
        sx={{ width: '100%', height: '36px' }}
        id="Period-selection"
        value={dateRange}
        onChange={handleChangePeriod}
        placeholder={'Select date range'}
      >
        {options.map(({ value, label }) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const defaultPresetOptions = [
  {
    value: pickerValuesMap.custom,
    label: pickerLabels[pickerValuesMap.custom],
  },
  {
    value: pickerValuesMap.last7Days,
    label: pickerLabels[pickerValuesMap.last7Days],
  },
  {
    value: pickerValuesMap.last30Days,
    label: pickerLabels[pickerValuesMap.last30Days],
  },
  {
    value: pickerValuesMap.lastYear,
    label: pickerLabels[pickerValuesMap.lastYear],
  },
  {
    value: pickerValuesMap.today,
    label: pickerLabels[pickerValuesMap.today],
  },
  {
    value: pickerValuesMap.tomorrow,
    label: pickerLabels[pickerValuesMap.tomorrow],
  },
  { value: pickerValuesMap.mtd, label: pickerLabels[pickerValuesMap.mtd] },
  { value: pickerValuesMap.ytd, label: pickerLabels[pickerValuesMap.ytd] },
  ...generateYearOptions(2),
];

export const DateRangePicker = ({
  fromDate,
  toDate,
  handleChangePeriod,
  handleChangeToDate,
  handleChangeFromDate,
  isCustomDateRange,
  dateRange,
  presetOptions,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2,
        width: '100%',
      }}
    >
      <Box sx={{ flex: '1 1 180px', minWidth: '180px' }}>
        <StartDatePicker
          fromDate={fromDate}
          handleChangeFromDate={handleChangeFromDate}
          toDate={toDate}
          isCustomDateRange={isCustomDateRange}
        />
      </Box>

      <Box sx={{ flex: '1 1 180px', minWidth: '180px' }}>
        <EndDatePicker
          toDate={toDate}
          handleChangeToDate={handleChangeToDate}
          fromDate={fromDate}
          isCustomDateRange={isCustomDateRange}
        />
      </Box>

      <Box sx={{ flex: '1 1 180px', minWidth: '180px' }}>
        <PeriodSelector
          options={presetOptions || defaultPresetOptions}
          dateRange={dateRange}
          handleChangePeriod={handleChangePeriod}
        />
      </Box>
    </Box>
  );
};
